import { useEffect, useState } from 'react';
import { getMe, logout } from './api/client';
import { SignIn } from './pages/SignIn';
import { SubmitPage } from './pages/SubmitPage';

// The auth state is one of: { status: 'loading' }, { status: 'anonymous' }, or
// { status: 'signed-in', me }.

/**
 * Reasons the BFF may append to the URL after a failed sign-in, and what to tell a
 * non-technical user about each. Anything unlisted gets the generic message.
 */
const AUTH_ERROR_MESSAGES = {
  tenant_not_allowed:
    'Your organisation is not yet set up for this portal. Please contact support@nics.ca to request access.',
  tenant_disabled:
    'Access for your organisation has been suspended. Please contact support@nics.ca to request access.',
  not_authorised:
    'You signed in successfully, but your account has not been given access to this portal. Ask your administrator to assign you the Submitter or Reader role.',
  access_denied: 'Sign-in was cancelled.',
  state: 'The sign-in took too long or was opened in a different window. Please try again.',
  provider_unavailable: 'Microsoft sign-in is temporarily unavailable. Please try again shortly.',
};

/** Read and clear ?auth_error= so a refresh does not show the same message again. */
function takeAuthError() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('auth_error');
  if (!code) return undefined;
  params.delete('auth_error');
  const query = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  return AUTH_ERROR_MESSAGES[code] ?? `Sign-in failed (${code}). Please try again.`;
}

export function App() {
  const [auth, setAuth] = useState({ status: 'loading' });
  const [authError] = useState(takeAuthError);
  const [loadError, setLoadError] = useState();

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (cancelled) return;
        setAuth(me ? { status: 'signed-in', me } : { status: 'anonymous' });
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not reach the portal server. Is it running?');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSignOut() {
    await logout();
    setAuth({ status: 'anonymous' });
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Client Request Portal</span>
        {auth.status === 'signed-in' && (
          <span className="who">
            {auth.me.name ?? auth.me.email ?? 'Signed in'}
            <button type="button" className="link" onClick={handleSignOut}>
              Sign out
            </button>
          </span>
        )}
      </header>

      <main>
        {loadError && <p className="alert alert--error">{loadError}</p>}
        {auth.status === 'loading' && !loadError && <p className="muted">Loading…</p>}
        {auth.status === 'anonymous' && <SignIn errorMessage={authError} />}
        {auth.status === 'signed-in' && <SubmitPage me={auth.me} />}
      </main>
    </div>
  );
}
